import React from 'react';
import { Box, Grid, Typography, useTheme, useMediaQuery, Paper } from '@mui/material';
import { getEducationList } from '../api/education';
import { useQuery } from '@tanstack/react-query';
import CustomThreeStar from '../components/Threejs/CustomThreeStar';

const EducationScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data, isError, isLoading, isFetched, refetch } = useQuery({
    queryKey: ['educationList'],
    queryFn: getEducationList,
  });

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', position: 'relative', mb: 10 }}>
      <CustomThreeStar />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? 2 : 4,
        }}
      >
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            mb: isMobile ? 4 : 6,
            color: theme.palette.text.primary,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Education
        </Typography>
        <Grid
          container
          spacing={isMobile ? 2 : 4}
          sx={{
            width: '100%',
            maxWidth: '1200px',
            mx: 'auto',
            justifyContent: 'center',
          }}
        >
          {data?.data?.data?.map((education) => (
            <Grid item key={education._id} xs={12} sm={6} md={4}>
              <Paper
                elevation={6}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  overflow: 'hidden',
                  transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                  background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.grey[200]})`,
                  boxShadow: `0px 8px 15px ${theme.palette.grey[300]}`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0px 12px 20px ${theme.palette.grey[400]}`,
                  },
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {/* Card Header - Institution Name */}
                <Box
                  sx={{
                    p: 2,
                    pb: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: theme.palette.mode === 'light'
                      ? theme.palette.primary.light
                      : theme.palette.primary.dark,
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {education.name}
                  </Typography>
                </Box>

                {/* Card Content */}
                <Box sx={{ p: 2, flexGrow: 1 }}>
                  {/* Location */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {education?.place}
                    </Typography>
                  </Box>

                  {/* Course & Percentage */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {education.course}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 1,
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: theme.palette.primary.light + '33',
                      fontWeight: 600,
                    }}
                  >
                    {education.marks_percentage}%
                  </Typography>
                </Box>

                {/* Card Footer */}
                <Box
                  sx={{
                    p: 2,
                    pt: 1.5,
                    mt: 'auto',
                    borderTop: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(145deg, ${theme.palette.grey[100]}, ${theme.palette.grey[200]})`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {education.presentDate}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default EducationScreen;
